import 'braid-design-system/reset';
import React, { useState, useEffect } from 'react';
import type { SharedRenderProps } from 'sku';

import { HirerAdPriceLookup } from './components/HirerAdPriceLookup/HirerAdPriceLookup';
import { AdvertiserNation, isNZ } from './utils/AdvertiserNation';
import { Environment, isLocal, isStaging } from './utils/Environment';
import { createHirerGraphClient } from './utils/HirerApolloClient';
import { ApolloProvider } from '@apollo/client';
import { useContractBalances, ContractType } from './services/contractsService';
import {
  sendOnLoadEvent,
  sendClassificationSelectedEvent,
  sendRoleTitleChangedEvent,
  sendLocationSelectedEvent,
  sendProductLinkClickedEvent,
  type PageInfo,
  sendPriceQueryEvent,
} from './utils/analytics/trackingEvent';
import { BraidProvider, Loader, Box } from 'braid-design-system';
import apacTheme from 'braid-design-system/themes/apac';
import seekJobs from 'braid-design-system/themes/seekJobs';
import { TrackingContext } from './context/trackingContext';
import { useHirerPricingModel } from './services/hirerAccountService';
import { PricingModel } from './utils/Product';
import {
  PricingContextProvider,
  type AppMode,
} from './context/PricingContextProvider';
import { VocabProvider } from '@vocab/react';
import { localizeRoutesForSite } from '../routes';
import {
  type SiteName,
  type Locale,
  getZoneFromSite,
  getLanguageFromLocale,
  type Language,
  siteConfig,
} from '@seek/melways-sites';
import { Route, Routes } from 'react-router-dom';
import { setLogger } from './utils/logger/logger';
import { createDatadogLogger } from './utils/datadogRUM/datadogRUM';
import { getLocalAccountInfo, getWindowLocation } from './utils/helper';
import {
  type HeaderFooterContext,
  type HeaderFooterContextInput,
  useHeaderFooterContextQuery,
} from './generated/graphql-types';
import { HeaderAndFooterWrapper } from './components/HeaderFooterWrapper/HeaderAndFooterWrapper';
import type { EmployerSiteName } from '../types';
import { AppContextProvider } from './context/AppContext';
import { ErrorPage } from './components/ErrorPage/ErrorPage';
import { UivBanner } from '@seek/uiv-banner';

interface AppProps {
  appMode: AppMode;
  advertiserNation: AdvertiserNation;
  environment: string;
  site: EmployerSiteName;
  language: string;
}

type AppWrapperProps = {
  site: EmployerSiteName;
  environment: Environment;
  advertiserNation: AdvertiserNation;
  buildVersion: string;
  branchName: string;
} & Pick<SharedRenderProps, 'language'>;

export const App = (props: AppProps) => {
  const { environment, advertiserNation, site, language, appMode } = props;
  const { search, pathname } = getWindowLocation();
  const headerFooterContextInput: HeaderFooterContextInput = {
    location: { search, pathname },
  };
  const {
    data: headerFooterData,
    error: headerFooterError,
    loading: headerFooterLoading,
  } = useHeaderFooterContextQuery({
    variables: { input: headerFooterContextInput },
  });

  const accountInfo: HeaderFooterContext = isLocal(environment)
    ? getLocalAccountInfo()
    : headerFooterData?.headerFooterContext;

  const advertiserId =
    headerFooterData?.currentAdvertiser?.__typename ===
    'CurrentAdvertiserContext'
      ? headerFooterData.currentAdvertiser.seekId
      : undefined;

  const [pageInfo, setPageInfo] = useState<PageInfo>();

  const { data: contractBalancesData, error: contractBalancesError } =
    useContractBalances(advertiserNation, advertiserId);

  const {
    loading: loadingPricingModel,
    data: pricingModel,
    error: pricingModelError,
  } = useHirerPricingModel(advertiserId);

  useEffect(() => {
    if (
      !pageInfo &&
      contractBalancesData &&
      !contractBalancesError &&
      advertiserId &&
      accountInfo &&
      !loadingPricingModel &&
      !pricingModelError
    ) {
      const defaultPricingModel = isNZ(advertiserNation)
        ? PricingModel.FIXED
        : PricingModel.HORIZON;

      setPageInfo({
        contracts: contractBalancesData.contractBalances,
        advertiserId,
        userId: accountInfo.user?.id || '',
        userEmail: accountInfo.identityContext?.email || '',
        userFullName: accountInfo.user?.name || '',
        appMode,
        pricingModel: pricingModel ?? defaultPricingModel,
        brand: siteConfig[site].classification.brand,
        country: siteConfig[site].classification.country,
        zone: getZoneFromSite(site),
        language: getLanguageFromLocale(language as Language),
      });
    }
  }, [
    contractBalancesData,
    contractBalancesError,
    appMode,
    accountInfo,
    advertiserId,
    pricingModel,
    loadingPricingModel,
    pricingModelError,
    site,
  ]);

  useEffect(() => {
    if (pageInfo) {
      sendOnLoadEvent(pageInfo);
    }
  }, [pageInfo]);

  const onClassificationSelected = () => {
    if (pageInfo) {
      sendClassificationSelectedEvent(pageInfo);
    }
  };
  const onRoleTitleUpdated = () => {
    if (pageInfo) {
      sendRoleTitleChangedEvent(pageInfo);
    }
  };
  const onLocationSelected = () => {
    if (pageInfo) {
      sendLocationSelectedEvent(pageInfo);
    }
  };
  const onProductLinkClicked = () => {
    if (pageInfo) {
      sendProductLinkClickedEvent(pageInfo);
    }
  };

  const onPriceQuery = (
    locationId: number,
    classification: string,
    subClassification: string,
  ) => {
    if (pageInfo) {
      sendPriceQueryEvent(
        pageInfo,
        locationId,
        classification,
        subClassification,
      );
    }
  };
  const hasLegacyContracts = contractBalancesData?.contractBalances.some(
    (contract) => contract.contractType !== ContractType.SEEK_CONTRACT,
  );

  const isOnVariablePricing =
    pricingModel === PricingModel.HORIZON && !hasLegacyContracts;

  const isLocalDevAuthenticationError =
    isLocal(environment) &&
    headerFooterError?.name === 'ApolloError' &&
    headerFooterError.graphQLErrors?.[0]?.extensions?.code ===
      'UNAUTHENTICATED';

  if (headerFooterError && !isLocalDevAuthenticationError) {
    return (
      <HeaderAndFooterWrapper
        accountInfo={accountInfo}
        site={site}
        isStaging={isStaging(environment)}
        locale={language as Locale}
      >
        <ErrorPage />
      </HeaderAndFooterWrapper>
    );
  }

  // TODO: check if we need to verify data on local
  if (headerFooterLoading || (!accountInfo && !headerFooterData)) {
    return (
      <HeaderAndFooterWrapper
        accountInfo={accountInfo}
        site={site}
        isStaging={isStaging(environment)}
        locale={language as Locale}
      >
        <Loader />
      </HeaderAndFooterWrapper>
    );
  }

  return (
    <PricingContextProvider
      appMode={appMode}
      isOnVariablePricing={isOnVariablePricing}
    >
      <TrackingContext.Provider
        value={{
          onClassificationSelected,
          onRoleTitleUpdated,
          onLocationSelected,
          onProductLinkClicked,
          onPriceQuery,
        }}
      >
        <HeaderAndFooterWrapper
          accountInfo={accountInfo}
          site={site}
          isStaging={isStaging(environment)}
          locale={language as Locale}
        >
          <HirerAdPriceLookup
            advertiserId={advertiserId}
            advertiserNation={advertiserNation}
          />
        </HeaderAndFooterWrapper>
      </TrackingContext.Provider>
    </PricingContextProvider>
  );
};

const AppWrapper = (props: AppWrapperProps) => {
  const {
    language,
    advertiserNation,
    environment,
    site,
    buildVersion,
    branchName,
  } = props;

  // Typecast here because of a typing mismatch between sku and melways
  const siteRoutes = localizeRoutesForSite(site as SiteName);

  useEffect(() => {
    setLogger(createDatadogLogger({ environment, buildVersion }));
  }, []);

  return (
    <VocabProvider language={language}>
      <Routes>
        {siteRoutes.map(({ name, route }) => {
          const appMode: AppMode =
            name === 'priceLookupPreview' ? 'PREVIEW' : 'DEFAULT';
          const apolloClient = createHirerGraphClient({
            appMode,
            site,
            environment,
          });

          return (
            <Route
              key={name}
              path={route}
              element={
                <ApolloProvider key={name} client={apolloClient}>
                  <BraidProvider theme={seekJobs}>
                    <AppContextProvider
                      language={language}
                      site={site}
                      environment={environment}
                    >
                      <UivBanner
                        applicationName="hirer-ad-price-lookup"
                        branch={branchName}
                        version={buildVersion}
                      />
                      <App
                        appMode={appMode}
                        advertiserNation={advertiserNation}
                        environment={environment}
                        site={site}
                        language={language}
                      />
                    </AppContextProvider>
                  </BraidProvider>
                </ApolloProvider>
              }
            />
          );
        })}
      </Routes>
    </VocabProvider>
  );
};
export default AppWrapper;
